import React, { useState, useEffect, useRef } from "react";
import DiscussionChat from "./DiscussionChat";
import { getAllDestinations, getDestinationMeetings } from "./api";
import { useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import OutlookChat from "./Outlook/OutlookChat";

function DiscussionTabs() {
  const { searchTerm } = useOutletContext(); // Get search term from context
  const [t] = useTranslation("global");

  const [destinations, setDestinations] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [activeDestination, setActiveDestination] = useState(null);
  const [activeMeeting, setActiveMeeting] = useState(null);
  const [activeTab, setActiveTab] = useState("tab1"); // Track active tab (missions or gmail)
  const [emails, setEmails] = useState([]); // Mock email data
  const [composing, setComposing] = useState(false); // Toggle compose form
  const [newEmail, setNewEmail] = useState({ to: "", subject: "", body: "" }); // New email state
  const tabsContainerRef = useRef(null);
  const [loading, setLoading] = useState({
    destinations: true,
    meetings: true,
    emails: true,
  });

  // Filter destinations based on search term
  const filteredDestinations = searchTerm
    ? destinations.filter(
        (destination) =>
          destination.destination_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (destination.clients?.client_name &&
            destination.clients.client_name
              .toLowerCase()
              .includes(searchTerm.toLowerCase()))
      )
    : destinations;

  // Mock email data with more Gmail-like structure
  useEffect(() => {
    const mockEmails = [
      {
        id: 1,
        from: "John Doe",
        email: "john@example.com",
        subject: "Meeting Tomorrow",
        snippet:
          "Hi, let's discuss the project tomorrow at 10 AM in the main conference room...",
        date: "Jul 16",
        read: false,
        labels: ["work"],
        starred: false,
      },
      {
        id: 2,
        from: "Jane Smith",
        email: "jane@example.com",
        subject: "Project Update",
        snippet:
          "Here's the latest update on the project. We've completed phase 1 and...",
        date: "Jul 15",
        read: true,
        labels: ["work", "important"],
        starred: true,
      },
      {
        id: 3,
        from: "Team Newsletter",
        email: "newsletter@company.com",
        subject: "Weekly Team Digest",
        snippet:
          "Here are this week's announcements: 1. Office will be closed on Friday...",
        date: "Jul 14",
        read: true,
        labels: ["newsletters"],
        starred: false,
      },
    ];
    setEmails(mockEmails);
    setLoading((prev) => ({ ...prev, emails: false }));
  }, []);

  // ... (keep your existing useEffect hooks and other functions)

  const handleStarEmail = (id, e) => {
    e.stopPropagation();
    setEmails(
      emails.map((email) =>
        email.id === id ? { ...email, starred: !email.starred } : email
      )
    );
  };

  const handleEmailClick = (id) => {
    setEmails(
      emails.map((email) =>
        email.id === id ? { ...email, read: true } : email
      )
    );
    // Here you would typically open the email in a reading pane
  };

  // Format date to be more Gmail-like
  const formatDate = (dateStr) => {
    // This is a simple formatter - you might want to use a library like date-fns
    return dateStr; // In a real app, you'd format this properly
  };
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const destinationsData = await getAllDestinations();
        setDestinations(destinationsData?.data || []);

        if (destinationsData?.data?.length > 0) {
          const firstDestination = destinationsData.data[0];
          setActiveDestination(firstDestination.id);
          await fetchMeetings(firstDestination.id);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
      } finally {
        setLoading((prev) => ({ ...prev, destinations: false }));
      }
    };

    fetchInitialData();
  }, []);

  const fetchMeetings = async (destinationId) => {
    try {
      setLoading((prev) => ({ ...prev, meetings: true }));
      const meetingsData = await getDestinationMeetings(destinationId);
      setMeetings(meetingsData?.data || []);

      if (meetingsData?.data?.length > 0) {
        setActiveMeeting(meetingsData.data[0].id);
      } else {
        setActiveMeeting(null);
      }
    } catch (error) {
      console.error("Error fetching meetings:", error);
    } finally {
      setLoading((prev) => ({ ...prev, meetings: false }));
    }
  };

  const handleDestinationChange = (destinationId) => {
    setActiveDestination(destinationId);
    fetchMeetings(destinationId);

    setTimeout(() => {
      const activeTab = document.querySelector(
        `button[data-id="${destinationId}"]`
      );
      if (activeTab && tabsContainerRef.current) {
        const container = tabsContainerRef.current;
        const tab = activeTab;
        const containerWidth = container.offsetWidth;
        const tabOffset = tab.offsetLeft;
        const tabWidth = tab.offsetWidth;

        container.scrollTo({
          left: tabOffset - containerWidth / 2 + tabWidth / 2,
          behavior: "smooth",
        });
      }
    }, 100);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleComposeEmail = () => {
    setComposing(true);
  };

  const handleSendEmail = (e) => {
    e.preventDefault();
    // Simulate sending email (replace with API call)
    console.log("Sending email:", newEmail);
    setEmails([
      ...emails,
      {
        id: emails.length + 1,
        from: "you@example.com",
        subject: newEmail.subject,
        body: newEmail.body,
        date: new Date().toISOString().split("T")[0],
      },
    ]);
    setNewEmail({ to: "", subject: "", body: "" });
    setComposing(false);
  };

  const handleCancelCompose = () => {
    setNewEmail({ to: "", subject: "", body: "" });
    setComposing(false);
  };

  const user = JSON.parse(sessionStorage.getItem("user"));
  if (loading.destinations || loading.emails) return <div>Loading...</div>;

  return (
    <div className="tabs-container container-fluid">
      <div className={`tabs-header`}>
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="meeting-title my-3">Discussions</h4>

          <button
            className={`btn moment-btn`}
            onClick={() => {
              window.open(
                "https://calendly.com/tektime/tektime-qu-est-ce-que-c-est ",
                "_blank"
              );
            }}
          >
            {t("request a demo")}
          </button>
        </div>

        <div className={`container-fluid`}>
          <div
            className="row align-items-center gutter-0"
            style={{ padding: "0 10px" }}
          >
            {/* Tabs Section */}
            <div
              className="col-lg-11 col-md-10 col-12 border-bottom tabs-meeting"
              style={{ borderBottom: "2px solid #F2F2F2" }}
            >
              <div className="tabs" style={{ overflowX: "auto" }}>
                <div className="d-flex">
                  <button
                    className={`tab ${activeTab === "tab1" ? "active" : ""}`}
                    onClick={() => setActiveTab("tab1")}
                  >
                    Missions
                  </button>
                  <button
                    className={`tab ${activeTab === "tab2" ? "active" : ""}`}
                    onClick={() => setActiveTab("tab2")}
                  >
                    Gmail
                  </button>
                  <button
                    className={`tab ${activeTab === "tab3" ? "active" : ""}`}
                    onClick={() => setActiveTab("tab3")}
                  >
                    {user?.outlook_user_info?.mail || "Outlook"}
                  </button>
                </div>
              </div>
            </div>

            <div
              className={`col-lg-1 col-md-2 col d-flex justify-content-end p-0 `}
            ></div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="complete-invite">
          {/* Missions Tab */}
         {activeTab === "tab1" && <div
            className={`tab-pane fade ${
              activeTab === "tab1" ? "show active" : ""
            }`}
            id="missions-content"
            role="tabpanel"
          >
        <div
  ref={tabsContainerRef}
  className="action-by-destination-scroll-container container"
  // style={{
  //   width: "100%",
  //   overflowX: "auto",
  //   overflowY: "hidden",
  //   WebkitOverflowScrolling: "touch",
  //   display: "flex",
  //   padding: "10px 0",
  //   marginBottom: "20px",
  //   scrollbarWidth: "thin",
  //   scrollbarColor: "#888 transparent",
  // }}
>
  <div
    // style={{
    //   display: "flex",
    //   padding: "0 16px",
    //   gap: "8px",
    //   minWidth: "100%", // Ensure container takes full width
    //   flexWrap: "nowrap",
    // }}
  >
    {filteredDestinations?.map((destination) => (
      <button
        key={destination.id}
        className={`tab ${
          activeDestination === destination.id ? "active" : ""
        }`}
        onClick={() => handleDestinationChange(destination.id)}
        style={{
       padding: "11px 11px",
                whiteSpace: "nowrap",
          background:
            activeDestination === destination.id ? "#007bff" : "#f8f9fa",
          color:
            activeDestination === destination.id ? "white" : "#495057",
             border: "none",
                cursor: "pointer",
                borderRadius: "13px",
                fontWeight: "bold",
          // cursor: "pointer",
          // whiteSpace: "nowrap",
          // fontSize: "14px",
          // fontWeight: "500",
          // position: "relative",
          maxWidth: "250px", // Limit maximum width of each tab
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
      >
        <div style={{ 
          display: "flex", 
          flexDirection: "column",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              overflow: "hidden",
            }}
          >
            {destination?.clients?.client_logo && (
              <img
                src={destination.clients.client_logo}
                alt="Client Logo"
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  flexShrink: 0,
                }}
              />
            )}
            <span style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}>
              {destination.destination_name}
            </span>
            {destination.unread_messages_count > 0 && (
              <span
                style={{
                  backgroundColor:
                    activeDestination === destination.id
                      ? "#ffffff"
                      : "#dc3545",
                  color:
                    activeDestination === destination.id
                      ? "#007bff"
                      : "#ffffff",
                  borderRadius: "10px",
                  padding: "2px 6px",
                  fontSize: "12px",
                  fontWeight: "bold",
                  minWidth: "20px",
                  textAlign: "center",
                  lineHeight: "1",
                  flexShrink: 0,
                }}
              >
                {destination.unread_messages_count}
              </span>
            )}
          </div>
          {destination.destination_end_date_time && (
            <div
              style={{
                fontSize: "12px",
                color:
                  activeDestination === destination.id
                    ? "rgba(255,255,255,0.8)"
                    : "#6c757d",
                marginTop: "4px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {new Date(
                destination.destination_end_date_time
              ).toLocaleDateString()}{" "}
              •{" "}
              {new Date(
                destination.destination_end_date_time
              ).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </div>
          )}
        </div>
      </button>
    ))}
  </div>
</div>  

            {loading.meetings ? (
              <div>Loading meetings...</div>
            ) : (
              <div className="accordion-content">
                {activeMeeting && (
                  <DiscussionChat
                    meetingId={activeMeeting}
                    meetingsData={meetings}
                    onMeetingsUpdate={(newMeetings) => setMeetings(newMeetings)}
                  />
                )}
              </div>
            )}
          </div>}

          {/* Gmail Tab */}
{activeTab=== "tab2" &&          <div
            className={`tab-pane fade ${
              activeTab === "tab2" ? "show active" : ""
            }`}
            id="gmail-content"
            role="tabpanel"
          >
            {loading.emails ? (
              <div className="d-flex justify-content-center my-5">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div
                className="gmail-container"
                style={{ fontFamily: "Roboto, Arial, sans-serif" }}
              >
                {/* Compose Button */}
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <button
                    className="btn btn-danger rounded-pill px-4 py-2 d-flex align-items-center"
                    onClick={handleComposeEmail}
                    style={{
                      boxShadow: "0 1px 2px 0 rgba(0,0,0,0.2)",
                      fontWeight: 500,
                    }}
                  >
                    <i className="bi bi-pencil-fill me-2"></i>
                    Compose
                  </button>

                  <div className="search-bar" style={{ width: "50%" }}>
                    <div className="input-group rounded-pill bg-light">
                      <span className="input-group-text bg-transparent border-0">
                        <i className="bi bi-search"></i>
                      </span>
                      <input
                        type="text"
                        className="form-control bg-light border-0"
                        placeholder="Search mail"
                        style={{ boxShadow: "none" }}
                      />
                    </div>
                  </div>
                </div>

                {/* Email List */}
                <div
                  className="email-list-container border rounded"
                  style={{ height: "70vh", overflowY: "auto" }}
                >
                  {/* Email Categories */}
                  <div className="d-flex border-bottom p-2">
                    <button className="btn btn-sm mx-1 px-3 active">
                      Primary
                    </button>
                    <button className="btn btn-sm mx-1 px-3">Social</button>
                    <button className="btn btn-sm mx-1 px-3">Promotions</button>
                  </div>

                  {/* Email List */}
                  <div className="list-group list-group-flush">
                    {emails.map((email) => (
                      <div
                        key={email.id}
                        className={`list-group-item list-group-item-action p-3 ${
                          !email.read ? "fw-bold" : ""
                        }`}
                        onClick={() => handleEmailClick(email.id)}
                        style={{
                          borderLeft: !email.read
                            ? "4px solid #d93025"
                            : "4px solid transparent",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {/* Star */}
                        <div
                          className="me-3"
                          onClick={(e) => handleStarEmail(email.id, e)}
                          style={{
                            color: email.starred ? "#fbbc04" : "#5f6368",
                          }}
                        >
                          <i
                            className={`bi ${
                              email.starred ? "bi-star-fill" : "bi-star"
                            }`}
                          ></i>
                        </div>

                        {/* Sender */}
                        <div className="me-4" style={{ minWidth: "200px" }}>
                          {email.from}
                        </div>

                        {/* Content */}
                        <div className="flex-grow-1">
                          <div className="d-flex">
                            <span className="me-2">{email.subject}</span>
                            <span className="text-muted text-truncate">
                              {email.snippet}
                            </span>
                          </div>
                        </div>

                        {/* Date */}
                        <div
                          className="ms-3 text-muted"
                          style={{ minWidth: "60px", textAlign: "right" }}
                        >
                          {formatDate(email.date)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Compose Email Modal */}
            {composing && (
              <div
                className="modal fade show"
                style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
              >
                <div
                  className="modal-dialog modal-xl"
                  style={{
                    position: "fixed",
                    bottom: "0",
                    right: "20px",
                    margin: "0",
                  }}
                >
                  <div className="modal-content" style={{ height: "70vh" }}>
                    <div className="modal-header bg-light py-2">
                      <h5 className="modal-title">New Message</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleCancelCompose}
                      ></button>
                    </div>
                    <div
                      className="modal-body p-0 d-flex flex-column"
                      style={{ flex: 1 }}
                    >
                      <form
                        onSubmit={handleSendEmail}
                        className="d-flex flex-column"
                        style={{ height: "100%" }}
                      >
                        <div className="p-3 border-bottom">
                          <input
                            type="email"
                            className="form-control border-0"
                            placeholder="To"
                            value={newEmail.to}
                            onChange={(e) =>
                              setNewEmail({ ...newEmail, to: e.target.value })
                            }
                            required
                          />
                        </div>
                        <div className="p-3 border-bottom">
                          <input
                            type="text"
                            className="form-control border-0"
                            placeholder="Subject"
                            value={newEmail.subject}
                            onChange={(e) =>
                              setNewEmail({
                                ...newEmail,
                                subject: e.target.value,
                              })
                            }
                          />
                        </div>
                        <div className="p-3 flex-grow-1">
                          <textarea
                            className="form-control border-0 h-100"
                            style={{ resize: "none" }}
                            value={newEmail.body}
                            onChange={(e) =>
                              setNewEmail({ ...newEmail, body: e.target.value })
                            }
                          ></textarea>
                        </div>
                        <div className="p-3 border-top d-flex justify-content-between align-items-center">
                          <button type="submit" className="btn btn-danger px-4">
                            Send
                          </button>
                          <button
                            type="button"
                            className="btn btn-light"
                            onClick={handleCancelCompose}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>}


          {/* Outlook Tab */}
          <div
            className={`tab-pane fade ${
              activeTab === "tab3" ? "show active" : ""
            }`}
            id="outlook-content"
            role="tabpanel"
          >
            <OutlookChat
              meetingId={activeMeeting}
              meetingsData={meetings}
              onMeetingsUpdate={(newMeetings) => setMeetings(newMeetings)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DiscussionTabs;
